import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "described"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEvent } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { usePopoverAnchor } from '../popover/popover-anchor.js';
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element that will be labelled or described by
 * a `Tooltip` component. This component will also be used as the reference to
 * position the tooltip on the screen.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const state = useToolTipState();
 * const props = useTooltipAnchor({ state });
 * <Role {...props}>Anchor</Role>
 * <Tooltip state={state}>Tooltip</Tooltip>
 * ```
 */

var useTooltipAnchor = createHook(function (_ref) {
  var _state$contentElement, _state$contentElement2;

  var state = _ref.state,
      described = _ref.described,
      props = _objectWithoutProperties(_ref, _excluded);

  var onFocusProp = props.onFocus;
  var onFocus = useEvent(function (event) {
    onFocusProp == null ? void 0 : onFocusProp(event);
    if (event.defaultPrevented) return; // When using multiple anchors for the same tooltip, we need to re-render
    // the tooltip to update its position.

    if (state.anchorRef.current !== event.currentTarget) {
      state.anchorRef.current = event.currentTarget;
      state.render();
    }

    state.show();
  });
  var onBlurProp = props.onBlur;
  var onBlur = useEvent(function (event) {
    onBlurProp == null ? void 0 : onBlurProp(event);
    if (event.defaultPrevented) return;
    state.hide();
  });
  var onMouseEnterProp = props.onMouseEnter;
  var onMouseEnter = useEvent(function (event) {
    onMouseEnterProp == null ? void 0 : onMouseEnterProp(event);
    if (event.defaultPrevented) return; // When using multiple anchors for the same tooltip, we need to re-render
    // the tooltip to update its position.

    if (state.anchorRef.current !== event.currentTarget) {
      state.anchorRef.current = event.currentTarget;
      state.render();
    }

    state.show();
  });
  var onMouseLeaveProp = props.onMouseLeave;
  var onMouseLeave = useEvent(function (event) {
    onMouseLeaveProp == null ? void 0 : onMouseLeaveProp(event);
    if (event.defaultPrevented) return;
    state.hide();
  });
  props = _objectSpread(_objectSpread({
    tabIndex: 0,
    "aria-labelledby": !described ? (_state$contentElement = state.contentElement) == null ? void 0 : _state$contentElement.id : undefined,
    "aria-describedby": described ? (_state$contentElement2 = state.contentElement) == null ? void 0 : _state$contentElement2.id : undefined
  }, props), {}, {
    onFocus: onFocus,
    onBlur: onBlur,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
  });
  props = usePopoverAnchor(_objectSpread({
    state: state
  }, props));
  return props;
});
/**
 * A component that renders an element that will be labelled or described by
 * a `Tooltip` component. This component will also be used as the reference to
 * position the tooltip on the screen.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const tooltip = useTooltipState();
 * <TooltipAnchor state={tooltip}>Anchor</TooltipAnchor>
 * <Tooltip state={tooltip}>Tooltip</Tooltip>
 * ```
 */

var TooltipAnchor = createComponent(function (props) {
  var htmlProps = useTooltipAnchor(props);
  return createElement("div", htmlProps);
});
export { TooltipAnchor, useTooltipAnchor };